import React, { useEffect, useRef, useState } from "react";
import ProcessLineageBadge from "@/components/library/ProcessLineageBadge";
import { useReadiness } from "../MBMRConfig/ReadinessContext";
import ApprovalStateIcon from "../MBMRConfig/ApprovalStateIcon";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Factory,
  Settings,
  Blend,
  Layers,
  Pill,
  Droplets,
  Trash2,
  Zap,
  Monitor,
  Snowflake,
  Pause,
  Thermometer,
  RotateCw,
  Filter,
  Download,
  Sun,
  Upload,
  Package,
  Scale,
  Scissors,
  FlaskConical,
  Coffee,
  Cookie,
  Wheat,
  Edit,
  Beaker,
  HardHat,
  TestTube,
  Activity,
  Target,
  CircleMinus,
  ChevronLeft,
  MoreVertical,
  ClipboardCheck,
  AlarmClock,
} from "lucide-react";
import { useParams, useLocation } from "react-router";
import {
  get_process_operation_by_recipe,
  get_process_with_operations,
  delete_operation_details,
  delete_process_details,
  get_approval_role_flow,
  get_process_data_v1,
  edit_process_data_v1,
} from "./mbmr_api";
import { Modal, Tooltip, Skeleton, Popconfirm, Checkbox, Button, Spin } from "antd";
import ProcessForm from "./ProcessForm";
import PageModal from '@/components/PageModal';
import EditRecipeModal from '../MBMRConfig/EditRecipeModal';
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessConfig,
  setSelectedOperationType,
  setSelectedStandardBlock,
} from "./slice/processConfig";
import { setOperationId } from "./slice/processConfig";
import { removeField, addField } from '@/store/reducers/headerContent';
import OperationButton from "./AddOperationConfig";
import { Dropdown, Menu } from "antd";
import { toggleRefresh } from '@/store/reducers/recipeConfigRefresh';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { useMbmrAlias } from '@/hooks/useAppSetting';
const getIcon = (operationType) => {
  const type = operationType?.toLowerCase().trim();
  const containsKeywords = (keywords) => {
    return keywords.some((keyword) => type.includes(keyword));
  };
  switch (true) {
    case containsKeywords(["mixing", "blending", "blend"]):
      return Blend;

    case containsKeywords(["coating", "enrobing"]):
      return Layers;

    case containsKeywords(["clearance", "line"]):
      return ClipboardCheck;

    case containsKeywords(["dispens", "Dispensing"]):
      return Scale;

    case containsKeywords(["tablet", "compression", "pill"]):
      return Pill;

    case containsKeywords(["sampling", "testing"]):
      return TestTube;

    case containsKeywords(["cleaning", "washing"]):
      return Trash2;

    case containsKeywords(["charging", "loading", "filling"]):
      return Upload;

    case containsKeywords(["monitoring", "checking"]):
      return Monitor;

    case containsKeywords(["cooling"]):
      return Snowflake;

    case containsKeywords(["heating", "boiling", "baking"]):
      return Thermometer;

    case containsKeywords(["stir"]):
      return RotateCw;

    case containsKeywords(["filtering", "filtration"]):
      return Filter;

    case containsKeywords(["drying"]):
      return Sun;

    case containsKeywords(["unloading", "discharge"]):
      return Download;

    case containsKeywords(["adding", "dosing"]):
      return Droplets;

    case containsKeywords(["packing", "packaging", "wrapping"]):
      return Package;

    case containsKeywords(["weighing"]):
      return Scale;

    case containsKeywords(["cutting", "milling"]):
      return Scissors;

    case containsKeywords(["distillation", "saponification"]):
      return FlaskConical;

    case containsKeywords(["cooking", "roasting"]):
      return Coffee;

    case containsKeywords(["wafer", "biscuit"]):
      return Cookie;

    case containsKeywords(["sift", "screening"]):
      return Wheat;

    case containsKeywords(["sterilization", "preparation"]):
      return Beaker;

    case containsKeywords(["quality", "inspection"]):
      return HardHat;

    case containsKeywords(["maintain"]):
      return Pause;

    case containsKeywords(["expelling"]):
      return Zap;

    case containsKeywords(["labeling", "coding"]):
      return Target;

    case containsKeywords(["settling"]):
      return Activity;

    case containsKeywords(["vacuum", "devacuum"]):
      return Activity;

    case containsKeywords(["releasing"]):
      return Upload;

    case containsKeywords(["transfer"]):
      return Activity;

    case containsKeywords(["sealing", "capping"]):
      return Package;

    case containsKeywords(["molding", "moulding", "shaping"]):
      return Activity;

    case containsKeywords(["storage"]):
      return Package;

    case containsKeywords(["alarm"]):
      return AlarmClock;

    default:
      return Settings;
  }
};


/** 2026-10-06 · the process row's dot says where the process is in its APPROVAL, not how ready
 *  it is (the readiness rail lists the gaps): orange while it is with its approvers, green when
 *  it has not been sent or has been approved, red when it came back for correction. The words
 *  are on hover. Read from the readiness payload's `lifecycle.processes[]`. */
const APPROVAL_DOT = {
  under_review:        { color: "#fa8c16", label: "Under review — sent for approval" },
  partially_submitted: { color: "#fa8c16", label: "Under review — sent for approval" },
  returned:            { color: "#ff4d4f", label: "Returned for correction" },
  approved:            { color: "#52c41a", label: "Approved" },
  draft:               { color: "#52c41a", label: "Not sent for approval" },
};
function ApprovalDot({ state }) {
  const meta = APPROVAL_DOT[state] || APPROVAL_DOT.draft;
  return (
    <span role="img" aria-label={meta.label} title={meta.label}
      style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: meta.color,
               marginRight: 6, verticalAlign: "middle", flex: "none" }} />
  );
}

const ProcessHierarchyTemp = ({
  setProcessLength,
  setIsLeftDrawerOpen,
  handleSubmitAddOperation,
  loading,
  setLoading,
  is_add_middle_modal,
  set_is_add_middle_modal,
  set_isAddOperation,
  isAddOperation,
  fromView,
  onApprovalStatusChange,
  // The host's own lock (the designer's read-only), for what the lifecycle cannot see — a
  // record finalised through the older flow, which batches are issued from.
  locked = false,
  // {operation_id: number of steps}, from the designer; absent on /process-config (no gate there).
  operationStepCounts = null,
}) => {
  const { isDark } = useTheme();
  // Readiness dots on the tree rows. The provider wraps the whole editor, so this is
  // null-safe when the tree renders outside it (library mode).
  const { lifecycle: readinessLifecycle } = useReadiness() || {};
  const mbmrAlias = useMbmrAlias();
  const headerContent = useSelector((state) => state.headerContent);
  const dispatch = useDispatch();

  // Dark mode colors
  const bgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const cardBgColor = isDark ? defaultColors.palette.gray[700] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#204448';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#204448';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#204448';
  const selectedBgColor = isDark ? '#0F172A' : '#204448';
  const buttonBgColor = isDark ? '#0F172A' : '#204448';
  const { recipe_id, libId, process_id } = useParams();
  // Process Library mode: configuring operations on a single recipe-less library
  // process. Derived from the URL so it survives a page refresh; navigation state is
  // only a fallback for the id.
  //
  // Two URLs reach this: the old `/process-config/library/:libId` wizard, and
  // `/library-designer/:process_id`, where the block is authored with the same designer
  // a record is. The param differs, the mode does not.
  const _location = useLocation();
  const is_library_mode = recipe_id === 'library' || !!process_id || !!_location?.state?.is_library;
  const library_process_id = libId || process_id || _location?.state?.library_process_id;
  const [data, set_data] = useState([]);
  const [expandedProcesses, setExpandedProcesses] = useState({});
  const [selectedProcess, setSelectedProcess] = useState(null);
  const selectedProcessRef = useRef(null);
  const [operationAddId, set_operationAddId] = useState(null);
  const [open, set_open] = useState(false);
  // Set the first time a record is found to hold no process, so the dialog that follows
  // opens once rather than on every reload.
  const openedForEmpty = useRef(false);
  // `recordLocked` is computed further down the component body, and this is read from a
  // fetch callback — a ref carries it there without reaching into the temporal dead zone.
  const lockedRef = useRef(false);
  const [editModal, setEditModal] = useState({ open: false, process: null });
  const [recipeModalMode, setRecipeModalMode] = useState(null); // null | 'edit' | 'view'
  const processInfo = useSelector((state) => state?.processConfig);
  const [selectedEditOp, set_selectedEditOp] = useState("");
  const refreshpage = useSelector((state) => state?.recipeconfigrefresh?.refresh);
  const processConfig = useSelector((state) => state?.processConfig);
  const [process_approval_status, setProcessApprovalStatus] = useState(false);
  // Recipe's packaging flag (from get_process_operation_by_recipe) — passed to
  // Add-Operation to filter op-types / copy-source processes by packaging type.
  const [recipe_is_packaging, setRecipeIsPackaging] = useState(false);
  const [approvalModal, setApprovalModal] = useState({ open: false, process: null, loading: false, saving: false });
  const [approval_role_list, set_approval_role_list] = useState([]);
  const [approvalSelectedRoles, setApprovalSelectedRoles] = useState([]);
  const [approvalProcessFullData, setApprovalProcessFullData] = useState(null);

  // Keep ref in sync so async API callbacks always read the latest selected process.
  useEffect(() => {
    selectedProcessRef.current = selectedProcess;
  }, [selectedProcess]);

  // The selected process's Process Type (if any) — passed to Add-Operation so
  // its "operations usable" whitelist (set on the Process Type) narrows the
  // Operation Type dropdown there.
  const selectedProcessTypeId = data?.find((p) => p.process_id === selectedProcess)?.process_type_id || null;

  const success_get_process_operation_details = (res) => {
    if (res.data.status === "success") {
      const data = res?.data?.data;
      const process_approval_status = res?.data?.is_process_approval
        ? res?.data?.is_process_approval
        : false;
      setProcessApprovalStatus(process_approval_status);
      onApprovalStatusChange?.(process_approval_status);
      // Recipe's packaging flag — kept in page state and passed to Add-Operation
      // (op-type list / copy-source processes) via props to filter by it.
      setRecipeIsPackaging(!!res?.data?.is_packaging);
      set_data(data);
      setProcessLength(data ? data?.length : 0);

      if (data && data?.length > 0) {
        // After a refresh (e.g. after edit), restore the previously selected
        // process. Only fall back to the last process on the very first load
        // (when nothing has been selected yet and the ref is null).
        const prevId = selectedProcessRef.current;
        const stillExists = prevId && data.some((p) => p.process_id === prevId);
        const procData = stillExists
          ? data.find((p) => p.process_id === prevId)
          : data[data.length - 1];

        setSelectedProcess(procData?.process_id);
        selectedProcessRef.current = procData?.process_id;
        onApprovalStatusChange?.(isProcessEditable(procData));

        dispatch(
          setProcessConfig({
            process: procData?.process,
            process_id: procData?.process_id,
            previous_operation_id: null,
            selected_standard_block: null,
            selected_operation_type: null,
          })
        );
      } else if (!fromView && !is_library_mode && !lockedRef.current && !openedForEmpty.current) {
        // 2026-09-28 · a record with no process at all opens on the form that makes one.
        //
        // Records are created empty now — nothing is seeded — so the first thing an author
        // sees is a rail with nothing in it and a page with nothing to do. The old
        // /process-config page opened its Add Process dialog in exactly this state, and the
        // reason holds: with no process there is only one next act, and making the author
        // find the button for it is a step that decides nothing.
        //
        // Once only. `openedForEmpty` guards the reload that follows a save and the
        // refreshes that follow an edit — an author who closes the dialog to look around
        // should not have it reopen underneath them.
        openedForEmpty.current = true;
        set_open(true);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const addProcessButton = () => {
    const isDisabled = !(data && data?.length > 0
      ? data[data?.length - 1] &&
        data[data?.length - 1].operation &&
        data[data?.length - 1].operation?.length > 0
      : data && data?.length === 0
      ? true
      : false);

    return (
      <button
        onClick={() => set_open(true)}
        disabled={isDisabled}
        className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-md transition-all duration-200 hover:shadow-lg ${
          isDisabled
            ? "cursor-not-allowed opacity-50"
            : "transition-transform duration-200"
        }`}
        style={{
          backgroundColor: buttonBgColor,
          color: "white",
        }}
      >
        <Plus className="w-4 h-4" />
        <span className="ml-2">Add Process</span>
      </button>
    );
  };

  const deleteConfirm = (record) => {
    const req = {
      operation_id: record?.operation_id,
      process_id: record?.processId || "",
    };
    delete_operation(req);
  };

  const openApprovalModal = (proc) => {
    setApprovalModal({ open: true, process: proc, loading: true, saving: false });
    setApprovalProcessFullData(null);
    setApprovalSelectedRoles([]);
    get_process_data_v1((res) => {
      if (res?.data?.status === 'success' && res?.data?.data) {
        const src = res.data.data;
        setApprovalProcessFullData(src);
        setApprovalSelectedRoles(src.approval_roles_id || []);
      }
      setApprovalModal((prev) => ({ ...prev, loading: false }));
    }, { process_id: proc.process_id });
  };

  const handleSaveApprovalRoles = () => {
    if (!approvalModal.process || !approvalProcessFullData) return;
    setApprovalModal((prev) => ({ ...prev, saving: true }));
    edit_process_data_v1((res) => {
      if (res?.data?.status === 'success') {
        toast.success(res?.data?.message || 'Approval roles updated');
        setApprovalModal({ open: false, process: null, loading: false, saving: false });
        dispatch(toggleRefresh());
      } else {
        toast.warning(res?.data?.message || 'Unable to update approval roles');
        setApprovalModal((prev) => ({ ...prev, saving: false }));
      }
    }, {
      ...approvalProcessFullData,
      process_id: approvalModal.process.process_id,
      approval_roles_id: approvalSelectedRoles,
      set_to_approval: true,
    });
  };

  const deleteProcessConfirm = (process) => {
    const req = {
      process_id: process?.process_id,
      recipe_id: recipe_id,
    };
    delete_process_details((res) => {
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message || "Process deleted successfully");
        dispatch(toggleRefresh());
      } else {
        toast.warning(res?.data?.message || "Unable to delete process");
      }
    }, req);
  };

  // Wrap the single-process response shape ({...process}) into the array shape
  // the recipe handler expects ([{...process}]).
  const success_get_library_process = (res) => {
    if (res?.data?.status === 'success' && res?.data?.data) {
      // get_process_with_operations returns a single process with an
      // `operations` (plural) key; the process-tree handler reads `operation`
      // (singular). Normalise so saved operations actually render in the list.
      const p = res.data.data;
      const normalised = { ...p, operation: p.operation || p.operations || [] };
      const wrapped = { ...res, data: { ...res.data, data: [normalised] } };
      success_get_process_operation_details(wrapped);
    } else {
      success_get_process_operation_details(res);
    }
  };

  // Single entry point to (re)load the process list — branches on library mode
  // so all refresh sites (initial load, after add/delete) stay consistent.
  // Fetch the process tree from the right source: a recipe-less library process
  // loads by process_id; a recipe loads by numeric recipe_id. Never call the
  // recipe API with a non-numeric id like the literal "library".
  const loadProcessTree = () => {
    if (is_library_mode) {
      if (library_process_id) {
        get_process_with_operations(success_get_library_process, {
          process_id: library_process_id,
        });
      }
      return;
    }
    if (recipe_id && !isNaN(Number(recipe_id))) {
      get_process_operation_by_recipe(success_get_process_operation_details, {
        recipe_id,
      });
    }
  };

  const reloadProcesses = () => {
    loadProcessTree();
  };

  useEffect(() => {
    setLoading(true);
    loadProcessTree();
    get_approval_role_flow((res) => {
      if (res?.data?.status === 'success') {
        set_approval_role_list(
          (res.data.data || [])
            .filter((item) => item.is_active !== false)
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .map((item) => ({ role_id: item.role_id, role: item.role_name, sequence: item.sequence }))
        );
      }
    }, { flow_type: 'process' });
  }, [recipe_id, refreshpage, is_library_mode, library_process_id]);

  // Library mode has no Product/MBMR (recipe-less) — the header title
  // normally shows "{Product} - {MBMR}", which for a library process was
  // rendering as a bare "-" since both fields are only ever populated by the
  // recipe flow. Give it a sensible label instead, without touching the
  // recipe/product header logic above. `data[0]?.process_name` covers a
  // refresh (location.state is lost); the location.state fallback covers the
  // very first render before the fetch resolves.
  useEffect(() => {
    if (!is_library_mode) return;
    const libraryProcessName = data?.[0]?.process_name || _location?.state?.process_name || '';
    dispatch(addField({ key: 'Product', value: 'Process Library' }));
    dispatch(addField({ key: 'MBMR', value: libraryProcessName }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_library_mode, data]);

  const toggleProcess = (processId, process) => {
    // to prevent multiple collapase process
    // setShowProcessPopover(null); // close after action
    if (selectedProcess && !expandedProcesses[processId]) {
      handleAddOperationToProcess(process);
    } else {
      handleAddOperationToProcess(process);
      selectProcess(process.process_id, process);
    }
    setExpandedProcesses((prev) => ({
      // ...prev,
      [processId]: !prev[processId],
    }));
  };

  const isOperationEditable = (op) => {
    if (op?.operation_status === 'Under Review') return false;
    if (op?.operation_status === 'Review & Approved') return false;
    return true;
  };

  // 2026-10-08 · the first operation of a process with no step yet, or null. While one exists,
  // no operation is added anywhere in that process (the server refuses it the same way): the
  // add dialog lets the author pick any operation to add after, so checking only the clicked
  // one left an empty operation behind. Without step counts (not the designer) nothing is held.
  const firstEmptyOperation = (process) => {
    if (!operationStepCounts) return null;
    return (process?.operation || []).find(
      (op) => !(operationStepCounts[String(op?.operation_id)] > 0)) || null;
  };

  // 2026-09-21 · the RECORD's own lifecycle, not just this process's. A record under review or
  // approved is locked whole — the banner above this panel already says so ("It becomes editable
  // again if it is returned for correction") — but the process rows went on offering Edit and
  // Delete, and the per-process check below only ever looked at `process_status`. An approved
  // MBMR is the controlled copy: it changes by a new version, never by an edit in place.
  const recordLocked = !!locked || (readinessLifecycle ? readinessLifecycle.editable === false : false);
  lockedRef.current = recordLocked;

  // Returns true when the process is fully editable (right panel unlocked)
  const isProcessEditable = (proc) => {
    if (recordLocked) return false;
    if (proc?.process_status === 'Under Review') return false;
    return true;
  };

  const selectProcess = (processId, process) => {
    const isSelected = selectedProcess === processId;
    setSelectedProcess(isSelected ? null : processId);
    set_selectedEditOp(null); // clear any highlighted operation when switching processes
    if (!isSelected) {
      set_isAddOperation(false);
      onApprovalStatusChange?.(isProcessEditable(process));
    }
    dispatch(
      setProcessConfig(
        isSelected
          ? { process: null, process_id: null, operation_id: null }
          : {
              process: process?.process_name,
              process_id: process?.process_id,
              operation_id: null, // always clear stale operation when switching process
              previous_operation_id:
                process?.operation?.length > 0
                  ? process?.operation[process?.operation?.length - 1]
                      .operation_id
                  : "",
              selected_standard_block: null,
              selected_operation_type: null,
            }
      )
    );
  };

  const toggleOperation = (process, operation) => {
    const checkOp =
      selectedEditOp === operation?.operation_id ? "" : operation?.operation_id;
    const isSelecting = checkOp && checkOp !== "";
    checkOp === "" ? set_isAddOperation(false) : set_isAddOperation(true);

    // Single dispatch — process_id and operation_id change together so the
    // form effect in SubOperationDetails fires exactly once with correct values.
    dispatch(
      setProcessConfig({
        process: process?.process_name,
        process_id: process?.process_id,
        previous_operation_id: null,
        operation_id: isSelecting ? operation?.operation_id : null,
        selected_standard_block: null,
        selected_operation_type: null,
        is_add_middle: false,
      })
    );
    setSelectedProcess(process?.process_id);
    set_operationAddId("");
    set_selectedEditOp(checkOp);

    if (operation?.operation_name) {
      if (isSelecting) {
        dispatch(addField({ key: "Process", value: process?.process_name || "" }));
        dispatch(addField({ key: "Operation", value: operation?.operation_name || "" }));
      } else {
        dispatch(removeField("Operation"));
      }
    }
    dispatch(setSelectedOperationType(null));
    dispatch(setSelectedStandardBlock(null));
  };

  // ######## handle api's ############
  const delete_operation = async (req) => {
    try {
      await delete_operation_details((res) => {
        console.log(res);
        if (res?.data?.status === "success") {
          toast.success(res?.data.message);
          reloadProcesses();
        } else {
          toast.warning(res?.data.message);
        }
      }, req);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    set_selectedEditOp(null);
    dispatch(setOperationId(null));
  }, [refreshpage]);

  // Selected from outside the tree (the designer selecting an operation it just made): light
  // the row and open its process, so the new operation is visible where it was added.
  useEffect(() => {
    const opId = processConfig?.operation_id;
    if (!opId || String(opId) === String(selectedEditOp)) return;
    const owner = (data || []).find((p) => (p?.operation || []).some((o) => String(o?.operation_id) === String(opId)));
    if (!owner) return;
    set_selectedEditOp(opId);
    setSelectedProcess(owner.process_id);
    setExpandedProcesses((prev) => (prev?.[owner.process_id] ? prev : { [owner.process_id]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processConfig?.operation_id, data]);

  useEffect(() => {
    if (!processConfig?.previous_operation_id) {
      set_operationAddId("");
    }
  }, [processConfig]);

  useEffect(() => {
    if (processInfo?.process) {
      dispatch(addField({ key: "Process", value: processInfo?.process || "" }));
    } else {
      dispatch(removeField("Process"));
      dispatch(removeField("Operation"));
    }
  }, [processInfo]);

  // Updated click outside handlers for both popovers

  // Handler for adding operation to process
  const handleAddOperationToProcess = (process) => {
    dispatch(setOperationId(null));
    set_selectedEditOp("");
    onApprovalStatusChange?.(isProcessEditable(process));
    dispatch(
      setProcessConfig({
        process: process?.process_name,
        process_id: process?.process_id,
        previous_operation_id: null,
        is_add_middle: false,
        selected_standard_block: null,
        selected_operation_type: null,
      })
    );
    setSelectedProcess(process?.process_id);
  };

  return (
    <div
      className="relative w-full h-full max-w-xl mx-auto shadow-md mt-15 rounded-lg overflow-y-auto border"
      style={{ borderColor: borderColor, backgroundColor: bgColor }}
    >
      {/* Header */}
      <div className="flex flex-col gap-3 shadow-sm rounded-lg p-2" style={{ backgroundColor: cardBgColor }}>
        <div className="flex items-center justify-between space-x-1">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="min-w-0 flex-1">
              <h1
                className="font-bold text-[16px] text-ellipsis overflow-hidden whitespace-nowrap"
                style={{ color: textColor }}
              >
                {headerContent?.Product} -
                <Tooltip
                  title={headerContent?.MBMR}
                  placement="bottom"
                  overlayInnerStyle={{
                    backgroundColor: isDark ? defaultColors.palette.gray[700] : '#ffffff',
                    color: isDark ? '#ffffff' : '#374151',
                  }}
                >
                  <span
                    onClick={() => setRecipeModalMode('view')}
                    className="cursor-pointer hover:underline"
                    style={{ color: 'inherit' }}
                  >
                    {headerContent?.MBMR?.length > 13
                      ? headerContent?.MBMR.slice(0, 13) + "..."
                      : headerContent?.MBMR}
                  </span>
                </Tooltip>
              </h1>
              <p
                className="text-l font-medium truncate"
                style={{ color: secondaryTextColor }}
              >
                {data?.length} processes
              </p>
            </div>
          </div>

          {/* Library mode has no recipe -> hide Edit Recipe. */}
          {/* Editing the record's own header is an edit like any other: not offered once the
              record is locked. */}
          {!fromView && !is_library_mode && !recordLocked && (
            <Tooltip title={`Edit ${mbmrAlias}`} placement="bottom">
              <button
                className="inline-flex items-center px-2 py-1.5 text-white text-xs font-medium rounded-lg transition-colors duration-200 hover:scale-105 flex-shrink-0"
                onClick={() => setRecipeModalMode('edit')}
                style={{ backgroundColor: buttonBgColor, color: 'white' }}
              >
                <Edit className="w-[18px] h-[18px]" />
              </button>
            </Tooltip>
          )}

          {/* Library mode: a library process is a single process, so no multi-process add.
              Locked record: a process cannot be added to an MBMR that is with its reviewers or
              approved, so the control is not offered at all. */}
          {!fromView && !is_library_mode && !recordLocked && (() => {
            // Why a process cannot be added now, or null. 2026-10-06 · a process with its
            // approvers blocks the next one: the record is in review and a new process would
            // change it under them. The tooltip names the process, so the author knows which
            // one to settle; the other two reasons are the ones the button already had.
            const underReview = (data || []).filter((p) => ["under_review", "partially_submitted"]
              .includes(readinessLifecycle?.byProcess?.[p?.process_id]?.state));
            const last = data && data.length ? data[data.length - 1] : null;
            const addBlocked = underReview.length
              ? `${underReview.map((p) => `"${p.process_name}"`).join(", ")} ${underReview.length === 1 ? "is" : "are"} under review. `
                + "Please get it reviewed (or returned for correction) before adding a new process."
              : last && !(last.operation?.length > 0)
                ? `Add an operation to "${last.process_name}" before adding the next process.`
                : null;
            // `process_approval_status` (the API's `is_process_approval`) is NOT a reason here any
            // more: it reads "the last process is approved" off approval-role rows, which the old
            // editor wrote the moment a process was created — so a record nobody had sent for
            // approval read as "approval pending" and could not grow. Only a process actually
            // under review blocks, judged by the lifecycle that ignores those rows.
            return (
              <Tooltip title={addBlocked || "Add Process"}>
                <span className="inline-flex flex-shrink-0">
                  <button
                    className="inline-flex items-center px-2 py-1.5 text-white text-xs font-medium rounded-lg transition-colors duration-200 hover:scale-105 flex-shrink-0"
                    onClick={() => !addBlocked && set_open(true)}
                    disabled={!!addBlocked}
                    data-testid="add-process"
                    style={{
                      color: "white",
                      backgroundColor: buttonBgColor,
                      opacity: addBlocked ? "0.6" : "1",
                      cursor: addBlocked ? "not-allowed" : "pointer",
                      // A disabled button swallows hover; the wrapping span carries the tooltip.
                      pointerEvents: addBlocked ? "none" : "auto",
                    }}
                  >
                    <Plus className="w-[18px] h-[18px]" />
                  </button>
                </span>
              </Tooltip>
            );
          })()}

          <button
            onClick={() => setIsLeftDrawerOpen((i) => !i)}
            className="inline-flexitems-center px-2 py-1.5 text-white text-xs font-medium rounded-lg transition-colors duration-200 flex-shrink-0"
            style={{
              backgroundColor: buttonBgColor,
              color: "white",
            }}
          >
            <ChevronLeft className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      {/* Process List */}
      <div className="space-y-2 p-3 overflow-visible border" style={{ borderColor: borderColor }}>
        <Skeleton loading={loading} active paragraph={{ rows: 8 }}>
          {data?.length > 0 ? (
            data?.map((process, index) => {
              const operations = process?.operation || [];
              const isExpanded = expandedProcesses[process.process_id];
              const isSelected = selectedProcess === process.process_id;
              const ProcessIcon = getIcon(process?.process_name);

              return (
                <div
                  key={process?.process_id}
                  className="rounded-lg shadow-sm border border-l-4 overflow-visible transition-all duration-200"
                  style={{
                    borderColor: borderColor,
                    borderLeftColor: selectedBgColor,
                    backgroundColor: isSelected ? selectedBgColor : cardBgColor,
                  }}
                >
                  <div
                    className="px-3 py-3.5 cursor-pointer transition-colors rounded-lg duration-200"
                    style={{
                      backgroundColor: isSelected ? selectedBgColor : "transparent",
                      color: isSelected ? "white" : textColor,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="flex items-center space-x-1.5 flex-shrink-0">
                          <div
                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                            style={{
                              backgroundColor: isSelected ? "white" : selectedBgColor,
                            }}
                          >
                            <ProcessIcon
                              className="w-3.5 h-3.5"
                              style={{
                                color: isSelected ? selectedBgColor : "white",
                              }}
                            />
                          </div>
                        </div>
                        <div
                          className="flex-1 min-w-0"
                          onClick={() =>
                            toggleProcess(process.process_id, process)
                          }
                        >
                          <div className="flex items-center space-x-2 mb-0.5">
                            <h2
                              className="text-sm font-semibold truncate flex-1 text-[16px]"
                              style={{
                                color: isSelected ? "white" : textColor,
                              }}
                            >
                              <ApprovalDot state={readinessLifecycle?.byProcess?.[process?.process_id]?.state} />
                              {process?.process_name}
                            </h2>
                            {/* LIB: library origin + drift, self-fetching; renders nothing when unlinked */}
                            <ProcessLineageBadge processId={process?.process_id} />
                          </div>
                          <p
                            className="text-xs truncate text-[13px]"
                            style={{
                              color: isSelected ? "white" : secondaryTextColor,
                            }}
                          >
                            Seq. {index + 1} • {operations?.length} operations
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-1.5">
                        {!fromView && (
                          <>
                            {/* Approval state (RDY-LIFE). This used to be a hard-coded
                                `ShieldCheck` — a tick on EVERY card in every state, so a
                                draft recipe read as fully approved at a glance while the
                                header above it said "Process Approvals: Not Provided". It is
                                now driven by the readiness payload's `lifecycle.processes[]`;
                                `process_status` from `get_process_operation_by_recipe` is not
                                usable here, because it comes back as an empty string for every
                                process on an approved recipe and on a draft one alike.

                                The icon keeps its old job — opening the approval-roles modal —
                                but only in the one state where sending is a legal next step. */}
                            {!is_library_mode && !recordLocked && (() => {
                              const lifecycleRow = readinessLifecycle?.byProcess?.[process?.process_id] || null;
                              const state = lifecycleRow?.state || 'draft';
                              const sendable = (state === 'draft' || state === 'returned') && operations.length > 0;
                              return (
                                <ApprovalStateIcon
                                  row={lifecycleRow}
                                  size={18}
                                  selected={isSelected}
                                  actionable={sendable}
                                  actionTitle={sendable ? 'click to set approval roles and send' : ''}
                                  onClick={() => openApprovalModal(process)}
                                />
                              );
                            })()}
                            {/* 2026-09-21 · once the record is locked the editing controls are
                                not shown at all, rather than shown greyed. A disabled Edit on
                                every row of an approved MBMR is a row of dead weight the eye
                                still has to read past; the record's own banner above already
                                says why it cannot be edited. Greyed-out is kept for the case
                                that is genuinely temporary and per-process (approval in
                                progress on ONE process, or a process with no operations yet) —
                                there, the control is about to come back, so it stays visible. */}
                            {recordLocked ? null : isProcessEditable(process) ? (
                              <Tooltip title="Edit Process">
                                <Edit
                                  className="w-[18px] h-[18px] cursor-pointer"
                                  style={{ color: isSelected ? "white" : textColor }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProcess(process.process_id);
                                    selectedProcessRef.current = process.process_id;
                                    setEditModal({ open: true, process });
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title={process?.process_status === 'Under Review'
                                ? 'Approval in progress — editing locked'
                                : 'Add operations before editing'}>
                                <Edit
                                  className="w-[18px] h-[18px]"
                                  style={{ color: isDark ? '#4b5563' : '#9ca3af', cursor: 'not-allowed' }}
                                />
                              </Tooltip>
                            )}
                            {/* Delete process: hidden in library mode (single process), and
                                disabled outright once the record is locked — a process cannot be
                                deleted out of an MBMR that is with its reviewers or approved. */}
                            {!is_library_mode && !recordLocked && (
                            <Popconfirm
                              title="Delete this process?"
                              description="All operations under this process will be deleted."
                              okText="Yes"
                              cancelText="No"
                              onConfirm={(e) => {
                                e?.stopPropagation?.();
                                deleteProcessConfirm(process);
                              }}
                              onCancel={(e) => e?.stopPropagation?.()}
                            >
                              <CircleMinus
                                className="w-[18px] h-[18px] cursor-pointer"
                                aria-label="Delete this process"
                                style={{ color: isSelected ? "white" : textColor }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                            )}
                          </>
                        )}
                        {/* <div
                        title="Process Menu"
                        className="transition-transform duration-200 relative"
                        ref={processPopoverRef}
                      >
                        <MoreVertical
                          className="w-3 h-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowProcessPopover(
                              showProcessPopover === process?.process_id
                                ? null
                                : process?.process_id
                            );
                            setShowOperationPopover(null);
                          }}
                        />
                        {showProcessPopover === process?.process_id && (
                          <div
                            ref={processPopoverRef}
                            className="absolute top-full mt-1 right-0 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg z-50 min-w-[160px] overflow-visible"
                          >
                            <div className="py-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 w-full text-left transition-colors duration-200"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                            </div>
                          </div>
                        )}
                      </div> */}
                        <div
                          style={{ color: isSelected ? "white" : textColor }}
                          className="cursor-pointer"
                          onClick={() =>
                            toggleProcess(process.process_id, process, process)
                          }
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-[18px] h-[18px]" />
                          ) : (
                            <ChevronRight className="w-[18px] h-[18px]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Operations */}
                  {isExpanded && (
                    <div
                      className="border-t rounded-lg max-h-[50vh] overflow-y-auto"
                      style={{
                        borderColor: isSelected
                          ? "rgba(255,255,255,0.3)"
                          : borderColor,
                        backgroundColor: cardBgColor,
                      }}
                    >
                      <div className="p-2 space-y-1.5">
                        {operations.length === 0 ? (
                          <div className="text-center" style={{ color: secondaryTextColor }}>No Operations.</div>
                        ) : (
                          operations?.map((operation) => {
                            const isOperationSelected =
                              selectedEditOp === operation?.operation_id;
                            const OperationIcon = getIcon(
                              operation?.operation_type
                            );

                            return (
                              <div
                                key={operation.operation_id}
                                className="rounded-lg shadow-sm border border-l-4 ml-3 transition-transform duration-200"
                                style={{
                                  borderColor: borderColor,
                                  borderLeftColor: selectedBgColor,
                                  backgroundColor: isOperationSelected
                                    ? selectedBgColor
                                    : cardBgColor,
                                }}
                              >
                                <div
                                  className="p-2 cursor-pointer transition-colors duration-200"
                                  onClick={() =>
                                    toggleOperation(process, operation)
                                  }
                                  style={{
                                    color: isOperationSelected
                                      ? "white"
                                      : textColor,
                                  }}
                                  // onMouseEnter={(e) => {
                                  //   if (!isOperationSelected) {
                                  //     e.target.style.backgroundColor =
                                  //       "#20444808";
                                  //   }
                                  // }}
                                  // onMouseLeave={(e) => {
                                  //   if (!isOperationSelected) {
                                  //     e.target.style.backgroundColor =
                                  //       "transparent";
                                  //   }
                                  // }}
                                >
                                  <div className="flex items-center justify-between" data-operation-id={operation?.operation_id}>
                                    <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                                      <div className="flex items-center space-x-1 flex-shrink-0">
                                        <div
                                          key={operation.operation_id}
                                          className="w-6 h-6 rounded-lg flex items-center justify-center"
                                          style={{
                                            backgroundColor: isOperationSelected
                                              ? "rgba(255,255,255,0.2)"
                                              : isDark ? "rgba(255,255,255,0.1)" : "#20444815",
                                          }}
                                        >
                                          <OperationIcon
                                            className="w-3 h-3"
                                            style={{
                                              color: isOperationSelected
                                                ? "#ffffff"
                                                : textColor,
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-1.5 mb-0.5">
                                          <span
                                            className=" font-medium truncate text-[13px] flex-1"
                                            style={{
                                              color: isOperationSelected
                                                ? "white"
                                                : textColor,
                                            }}
                                          >
                                            {operation?.operation_type}
                                          </span>
                                        </div>
                                        <p
                                          className=" truncate text-[12px]"
                                          style={{
                                            color: isOperationSelected
                                              ? "white"
                                              : secondaryTextColor,
                                          }}
                                        >
                                          {/* 2026-10-06 · no readiness dot on an operation row: the
                                              dot is the process's (one per process row above); an
                                              operation's own gaps are listed in the readiness rail. */}
                                          {/* The name being typed in the designer's operation form, echoed
                                              here for the selected row until it is saved. */}
                                          {String(operation?.operation_id) === String(processConfig?.operation_id)
                                            && typeof processConfig?.draft_operation_name === "string"
                                            ? (processConfig.draft_operation_name || "—")
                                            : operation?.operation_name}
                                        </p>
                                      </div>
                                      {/* The operation's own menu is Edit / Delete and nothing
                                          else, so on a locked record there is nothing left for
                                          it to offer — the trigger goes rather than sitting
                                          there disabled on every operation of the record. */}
                                      {recordLocked ? null : (
                                      <div>
                                        <Dropdown
                                          key={operation?.operation_id}
                                          trigger={["click"]}
                                          placement="bottomRight"
                                          overlayStyle={{
                                            zIndex: 9999,
                                            backgroundColor: isDark ? defaultColors.palette.gray[700] : "white",
                                          }}
                                          disabled={fromView || !isProcessEditable(process) || !isOperationEditable(operation)}
                                          menu={{
                                            style: {
                                              backgroundColor: isDark ? defaultColors.palette.gray[700] : "white",
                                              borderRadius: "6px",
                                              boxShadow:
                                                "0 2px 8px rgba(0,0,0,0.15)",
                                              zIndex: 9999,
                                            },
                                            items: [
                                              {
                                                key: "add",
                                                // 2026-10-07 · the next operation is added once this one has a step:
                                                // an operation with nothing in it is not finished. 2026-10-08 · any
                                                // empty operation of the process holds it (firstEmptyOperation).
                                                disabled: !!firstEmptyOperation(process),
                                                label: (
                                                  <Tooltip placement="left" title={firstEmptyOperation(process)
                                                    ? `Add at least one step to "${firstEmptyOperation(process)?.operation_name || "this operation"}" before adding the next operation.` : ""}>
                                                    <span className="flex items-center gap-2" style={{ color: isDark ? '#ffffff' : '#374151' }}>
                                                      <Plus className="w-4 h-4" />
                                                      Add Operation
                                                    </span>
                                                  </Tooltip>
                                                ),
                                                onClick: (e) => {
                                                  e.domEvent.stopPropagation();
                                                  const checkOp =
                                                    operation?.operation_id ===
                                                    operationAddId
                                                      ? ""
                                                      : operation?.operation_id;

                                                  set_operationAddId(checkOp);
                                                  set_selectedEditOp("");

                                                  dispatch(
                                                    setProcessConfig({
                                                      process: process?.process_name,
                                                      process_id:
                                                        process?.process_id,
                                                      operation_id: null,
                                                      previous_operation_id:
                                                        operation?.operation_id
                                                          ? operation?.operation_id
                                                          : null,
                                                      selected_standard_block:
                                                        null,
                                                      selected_operation_type:
                                                        null,
                                                      is_add_middle: true,
                                                    })
                                                  );

                                                  if (operation?.operation_name) {
                                                    if (checkOp) {
                                                      dispatch(
                                                        addField({
                                                          key: "Process",
                                                          value:
                                                            process?.process_name ||
                                                            "",
                                                        })
                                                      );
                                                      dispatch(
                                                        addField({
                                                          key: "Operation",
                                                          value:
                                                            operation?.operation ||
                                                            "",
                                                        })
                                                      );
                                                      set_is_add_middle_modal(
                                                        true
                                                      );
                                                    } else {
                                                      set_is_add_middle_modal(
                                                        false
                                                      );
                                                      dispatch(
                                                        setOperationId(null)
                                                      );
                                                      dispatch(
                                                        removeField("Operation")
                                                      );
                                                    }
                                                  }

                                                  setSelectedProcess(
                                                    process?.process_id
                                                  );
                                                  set_isAddOperation(false); // if add operation form is open so close it
                                                },
                                              },
                                              {
                                                key: "delete",
                                                label: (
                                                  <Popconfirm
                                                    title="Are you sure you want to delete this operation?"
                                                    okText="Yes"
                                                    cancelText="No"
                                                    placement="right"
                                                    onConfirm={(e) => {
                                                      deleteConfirm({
                                                        processId:
                                                          process.process_id,
                                                        operation_id:
                                                          operation?.operation_id,
                                                      });
                                                      // your delete logic here
                                                    }}
                                                    onCancel={(e) =>
                                                      e?.stopPropagation()
                                                    }
                                                  >
                                                    <span
                                                      className="flex items-center gap-2 text-red-600"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      } // stop bubbling so menu stays open until confirm
                                                    >
                                                      <CircleMinus className="w-4 h-4" aria-hidden="true" />
                                                      Delete Operation
                                                    </span>
                                                  </Popconfirm>
                                                ),
                                              },
                                            ],
                                          }}
                                          className="transition-transform duration-200 hover:scale-110"
                                        >
                                          <button
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center px-2 py-1.5 text-xs font-medium rounded-lg transition-colors duration-200"
                                            style={{
                                              backgroundColor: selectedEditOp === operation?.operation_id
                                                ? "white"
                                                : operationAddId === operation?.operation_id
                                                ? "#166534"
                                                : buttonBgColor,
                                              color: selectedEditOp === operation?.operation_id
                                                ? selectedBgColor
                                                : "white",
                                            }}
                                          >
                                            <MoreVertical className="w-3 h-3" />
                                          </button>
                                        </Dropdown>
                                      </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div
              className="rounded-lg shadow-sm border p-4"
              style={{ borderColor: borderColor, backgroundColor: cardBgColor }}
            >
              <div className="flex flex-col h-[65vh] items-center justify-center text-center space-y-4 max-w-md mx-auto">
                {/* Icon Circle */}
                <div
                  className="w-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#20444810" }}
                >
                  <Factory className="w-8 h-8" style={{ color: textColor }} />
                </div>

                <div>
                  <p
                    className="text-2xl font-bold mb-3"
                    style={{ color: textColor }}
                  >
                    No Processes Found
                  </p>
                  <p className="text-base mb-5" style={{ color: secondaryTextColor }}>
                    Start by adding your first manufacturing process
                  </p>
                </div>
                {/* if its view page then hide button */}
                {!fromView && addProcessButton()}
              </div>
            </div>
          )}
        </Skeleton>
      </div>
      <OperationButton
        isModalOpen={is_add_middle_modal}
        setIsModalOpen={set_is_add_middle_modal}
        onSubmit={handleSubmitAddOperation}
        recipeIsPackaging={recipe_is_packaging}
        processTypeId={selectedProcessTypeId}
      />

      <PageModal
        open={open}
        onCancel={() => set_open(false)}
        icon={<Plus size={22} />}
        title="Add Process"
        description="Add a process to this recipe"
        destroyOnHidden
        width={720}
      >
        <ProcessForm onCancel={() => set_open(false)} />
      </PageModal>

      <PageModal
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, process: null })}
        icon={<Edit size={22} />}
        title="Edit Process"
        description="Edit this process"
        destroyOnHidden
        width={720}
      >
        <ProcessForm
          editProcess={editModal.process}
          onCancel={() => setEditModal({ open: false, process: null })}
        />
      </PageModal>

      <EditRecipeModal
        open={!!recipeModalMode}
        viewOnly={recipeModalMode === 'view'}
        recipeId={recipe_id}
        onClose={() => setRecipeModalMode(null)}
        onSuccess={() => dispatch(toggleRefresh())}
      />

      <Modal
        title={
          <span style={{ color: textColor }}>
            Approvals — {approvalModal.process?.process_name || ''}
          </span>
        }
        open={approvalModal.open}
        onCancel={() => setApprovalModal({ open: false, process: null, loading: false, saving: false })}
        destroyOnHidden
        centered
        width={440}
        styles={{
          content: { backgroundColor: cardBgColor },
          header: { backgroundColor: cardBgColor },
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setApprovalModal({ open: false, process: null, loading: false, saving: false })}
              disabled={approvalModal.saving}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={approvalModal.saving}
              disabled={approvalModal.loading || !approvalProcessFullData}
              onClick={handleSaveApprovalRoles}
              style={{ backgroundColor: buttonBgColor }}
            >
              Save
            </Button>
          </div>
        }
      >
        {approvalModal.loading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : (
          <div>
            <p className="text-sm mb-3" style={{ color: secondaryTextColor }}>
              Select which roles must approve this process before it can proceed.
            </p>
            {approval_role_list.length === 0 ? (
              <span style={{ color: '#64748b' }}>No approval roles configured</span>
            ) : (
              <Checkbox.Group
                options={approval_role_list.map((i) => ({ label: i.role, value: i.role_id }))}
                value={approvalSelectedRoles}
                onChange={(val) => setApprovalSelectedRoles(val)}
                className="flex flex-col gap-2"
              />
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ProcessHierarchyTemp;
